<template>
  <b-nav-item-dropdown
    ref="navdropdown"
    class="dropdown-notification mr-25"
    menu-class="dropdown-menu-media"
    right
    @show="onShow"
  >
    <template #button-content>
      <feather-icon
        :badge="unreadNotificationCount > 99 ? '99+' : unreadNotificationCount"
        badge-classes="bg-danger"
        class="text-body"
        icon="BellIcon"
        size="21"
      />
    </template>

    <!-- Header -->
    <li class="dropdown-menu-header">
      <div class="dropdown-header d-flex">
        <h4 class="notification-title mb-0 mr-auto">
          {{ $t("general.notifications") }}
        </h4>
        <b-link @click="goToNotificationSetting">
          <feather-icon class="cursor-pointer" icon="SettingsIcon" size="18" />
        </b-link>
      </div>
    </li>

    <div class="scrollable-container media-list scroll-area overflow-y">
      <b-link
        v-for="(notification, idx) in notifications"
        :key="`notification-${idx}`"
        :class="{
          unread: !notification.isRead,
        }"
        @click="readNotification(notification, idx)"
      >
        <b-media>
          <template #aside>
            <b-avatar size="32" variant="light-primary">
              <feather-icon
                v-if="notification.dataType == 'operator'"
                icon="UserPlusIcon"
              />
              <feather-icon
                v-else-if="notification.dataType == 'service_system'"
                icon="CastIcon"
              />
              <feather-icon v-else icon="MessageSquareIcon" />
            </b-avatar>
          </template>
          <p class="media-heading">
            <span class="font-weight-bolder">
              {{ getNotificationTitle(notification) }}
            </span>
            <small class="notification-text float-right">
              {{ notification.createdAt | formatShortDate }}
            </small>
          </p>
          <small class="notification-text overflow-hidden">
            <span class="d-block text-truncate" style="max-width: 250px;">
              {{ getNotificationDescription(notification) }}
            </span>
          </small>
        </b-media>
      </b-link>
      <div class="infinite-wrapper">
        <infinite-loading
          ref="notificationInfiniteLoading"
          :key="key"
          @infinite="index"
        >
          <div slot="spinner" class="spinner-container">
            <b-spinner label="Spinning" />
          </div>
          <div slot="no-more" />
          <div slot="no-results" />
        </infinite-loading>
      </div>
    </div>

    <!-- Cart Footer -->
    <li class="dropdown-menu-footer" v-if="unreadNotificationCount > 0">
      <b-button
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        variant="primary"
        block
        @click="readAllNotification"
      >
        {{ $t("button.readAllNotification") }}
      </b-button>
    </li>
    <audio
      ref="notificationSound"
      class="hidden"
      controls="controls"
      autobuffer="autobuffer"
      autoplay="autoplay"
    >
      <source
        src="data:audio/mp3;base64,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"
      />
    </audio>

    <audio
      ref="notifyVoiceAlert"
      class="hidden"
      controls="controls"
      autobuffer="autobuffer"
    >
      <source :src="wavSource" type="audio/wav" />
    </audio>
  </b-nav-item-dropdown>
</template>

<script>
import {
  BNavItemDropdown,
  BBadge,
  BMedia,
  BLink,
  BAvatar,
  BButton,
  BFormCheckbox,
  BSpinner,
  BListGroup,
  BListGroupItem,
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import InfiniteLoading from "vue-infinite-loading";
import Repository from "@/repositories/RepositoryFactory";
import { connectSocket } from "@/libs/socket";
import { e2eDecrypt } from "@/libs/crypto-js";
import chatSidebar from "@/navigation/vertical/menu/chat";
import serviceSidebar from "@/navigation/vertical/menu/service";//chatPartner
import ToastificationAvatar from "@core/components/toastification/ToastificationAvatar.vue";
import { bus } from "@/libs/bus";
import useJwt from "@/auth/jwt/useJwt";
import useAppStore from "@/store-pinia/useAppStore";

const ChatChannelRepository = Repository.get("chatChannel");
const NotificationRepository = Repository.get("notification");
const ChatAssignmentRepository = Repository.get("chatAssignmentRepository");
const SpeechAlertRepository = Repository.get("speechAlertRepository");
const ServiceSystemRepository = Repository.get('serviceSystem');

export default {
  components: {
    BNavItemDropdown,
    BBadge,
    BMedia,
    BLink,
    BAvatar,
    BButton,
    BFormCheckbox,
    BSpinner,
    BListGroup,
    BListGroupItem,

    InfiniteLoading,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      unreadNotificationCount: 0,
      unreadConversation: 0,
      // unseenServiceTicket: 0,
      notifications: [],
      limit: 20,
      page: 1,
      key: 1,
      isAutoAssignOrRanking: false,
      messageFilter: {
        operator: false,
      },

      socket: null,
      chatSidebar,
      serviceSidebar,

      originalTitle: document.title,
      notifyInterval: null,
      displayNotifyCount: false,
      wavSource: "",
    };
  },
  computed: {
    userData() {
      return this.$store.state.profile.email || this.$store.state.profile.phone
        ? this.$store.state.profile
        : JSON.parse(
            localStorage.getItem(useJwt.jwtConfig.storageUserDataKeyName)
          );
    },
    notifyId() {
      return this.$route.query.notifyId;
    },

    documentTitle() {
      return this.unreadConversation > 0
        ? `(${this.unreadConversation}) ${this.originalTitle}`
        : this.originalTitle;
    },
    locale() {
      return this.$i18n.locale.split("-")[0];
    }
  },
  watch: {
    unreadConversation(value) {
      this.chatSidebar[0].tag = value > 99 ? "99+" : value;
      this.chatSidebar = [...this.chatSidebar];
      document.title = this.documentTitle;
      this.displayNotifyCount = true;
    },
    // unseenServiceTicket(value) {
    //   this.serviceSidebar[0].tag = value > 99 ? "99+" : value;
    //   this.serviceSidebar = [...this.serviceSidebar];
    // },
    notifyId(value) {
      if (value) {
        this.read();
      }
    },
  },
  mounted() {
    this.chatSidebar[0].tag = 0;
    this.countUnread();
    this.countUnreadConversation();
    // this.getCountUnseen();

    window.addEventListener("focus", this.handleFocus);
    window.addEventListener("blur", this.handleBlur);
  },
  beforeDestroy() {
    if (this.socket) this.socket.disconnect();
  },
  destroyed() {
    window.removeEventListener("focus", this.handleFocus);
    window.removeEventListener("blur", this.handleBlur);
  },
  created() {
    this.connectSocket();
    if (this.notifyId) {
      this.read();
    }
  },
  methods: {
    handleFocus() {
      this.$nextTick(() => {
        document.title = this.documentTitle;
        this.displayNotifyCount = false;
      });
      clearInterval(this.notifyInterval);
    },
    handleBlur() {
      this.notifyInterval = setInterval(() => {
        if (!this.displayNotifyCount) return;
        document.title =
          this.unreadConversation > 0
            ? `(${this.unreadConversation}) New Message`
            : this.originalTitle;
        setTimeout(() => {
          this.$nextTick(() => {
            document.title = this.documentTitle;
          });
        }, 1000);
      }, 2000);
    },
    async read() {
      await NotificationRepository.read({
        notificationId: this.notifyId,
      });
      this.countUnread();
    },
    onShow() {
      this.notifications = [];
      this.page = 1;
      this.key++;
    },
    connectSocket() {
      this.socket = connectSocket();

      this.socket.on("connect", () => {
        if (this.socket.id) {
          this.online();
          this.socket.emit("join", {
            event: `operator:${this.userData.merchantId}:${this.userData.id}`,
          });

          this.socket.emit("join", {
            event: `${this.userData.merchantId}:channels`,
          });
          this.socket.emit("join", { event: this.userData.merchantId });
          this.socket.emit("join", { event: this.userData.id });
          this.listenSocket();
        }
      });
    },
    online() {
      if (this.$store.state.profile.isOffline) return;
      ChatChannelRepository.online({
        socketId: this.socket.id,
      })
        .then((response) => {})
        .catch((error) => {});
    },
    listenSocket() {
      this.socket.on("chat:actions", (data) => {
        if (data.data) {
          if(data.type === 'object' && Object.keys(data.data).length === 0) {
            return;
          }
          data.data = e2eDecrypt(data.data);
          try {
            data.data = JSON.parse(data.data);
          } catch (error) {
            console.log("Error[chat:actions] in parsing data ", error, data);
          }
        }

        if (data.type == 'bind-account') {
          this.appStore.setBoundTelegram(data.data.isBindTelegram)
        }

        if (data.type == "notification") {
          if (!data.data) {
            return;
          }

          if (this.unreadNotificationCount != data.data.unreadNotification) {
            this.unreadNotificationCount = data.data.unreadNotification;
            this.$refs.notificationSound.play();
          }
        } else if (data.type == "online") {
          if (this.socket.id) {
            this.online();
          }
        } else if (data.type == "channel" || data.type == "seen") {
          this.countUnreadConversation();

          if (!data.data) {
            return;
          }

          const messageCount = 0;
          let titleInterval;
          let blinkInterval;

          if (data.type == "channel") {
            try {
              const channel = data.data;
              if (
                channel.operatorAssigned === this.userData.id &&
                channel.latestMessage?.participantType === "customer"
              ) {
                this.$nextTick(() => {
                  if (this.$refs.notificationSound) {
                    this.$refs.notificationSound.play();
                  }
                });
              }
            } catch (e) {}
          }
        } else if (data.type == "voiceNotifyOperator") {
          const document = data.data;
          let { locale } = this.$i18n;
          if (locale == "zh-cn") {
            locale = "zh";
          }
          this.flashAlert(locale, document);
        } else if (data.type == "serviceVoiceNotifyOperator") {
          const document = data.data;
          let { locale } = this.$i18n;
          if (locale == "zh-cn") {
            locale = "zh";
          }
          this.flashVoiceAlertServiceToOperator(locale, document);
        }else if (data.type == 'hasNewServiceTicket') {
          console.log('hasNewServiceTicket', data.data);
          const document = data.data;
          this.flashForNewServiceTicket(document)
        }
        // else if (data.type == 'badgeCountServiceTicket') {
        //   console.log('badgeCountServiceTicket', data.data);
        //   const document = data.data;
        //   this.unseenServiceTicket = document?.unseenCount || 0;
        // }
        else if (data.type == 'handoverServiceTicket') {
          const document = data.data;
          this.flashHandoverServiceTicket(document)
        } else if (data.type === 'update-profile') {
          const profileData = data.data;
          const hasAllProjects = profileData.projects?.some(p => p.projectId === 'ALL_PROJECT')
          const hasAllCategories = profileData.serviceCateAndType?.some(c => c.serviceCategoryCode === 'ALL_CATEGORY')
          const hasAllTypes = profileData.serviceCateAndType?.some(c => c.serviceTypes?.some(t => t.code === 'ALL_TYPE'))
          const hasAllSubTypes = profileData.serviceCateAndType?.some(c => c.serviceTypes?.some(t => t.subTypes?.some(st => st.code === 'ALL_SUBTYPE')))

          // Get projects list
          const getProjects = () => {
            if (hasAllProjects) {
              return ['ALL_PROJECT']
            }
            return profileData.projects?.map(p => p.projectId).filter(Boolean) || []
          }

          // Get service categories list
          const getServiceCategories = () => {
            if (hasAllCategories) {
              return ['ALL_CATEGORY']
            }
            return profileData.serviceCateAndType?.map(c => c.serviceCategoryCode).filter(Boolean) || []
          }

          // Get service types list
          const getServiceTypes = () => {
            if (hasAllTypes) {
              return ['ALL_TYPE']
            }
            return profileData.serviceCateAndType?.flatMap(c => c.serviceTypes?.map(t => t.code).filter(Boolean)).filter(code => code !== 'ALL_TYPE') || []
          }

          // Get service sub types list
          const getServiceSubTypes = () => {
            if (hasAllSubTypes) {
              return ['ALL_SUBTYPE']
            }
            return profileData.serviceCateAndType?.flatMap(c => c.serviceTypes?.flatMap(t => t.subTypes?.map(st => st.code).filter(Boolean)).filter(code => code !== 'ALL_SUBTYPE')) || []
          }

          // Set allowed permissions in store
          this.appStore.setAllowed({
            projects: getProjects(),
            serviceCategories: getServiceCategories(),
            serviceTypes: getServiceTypes(),
            serviceSubTypes: getServiceSubTypes(),
          })
        }
      });
      this.socket.on("chatme.requestResolveRoom", (data) => {
        if (data.data) {
          data.data = JSON.parse(data.data);
          const avatar = data.data.avatarUrl
            ? `${data.data.avatarUrl}?width=50`
            : null;

          this.$toast({
            component: ToastificationAvatar,
            position: "top-right",
            timeout: 10000,
            props: {
              title: this.$t("alert.invitation"),
              avatarUrl: avatar,
              icon: `AirplayIcon`,
              variant: "success",
              text: this.$t("alert.userRequestForHelp", {
                name: data.data.name,
              }),
            },
          });
        }
      });
    },
    flashAlert(locale, document) {
      let title = "";
      let icon = "";
      let variant = "";
      let text = "";
      let message = "";
      if (document.alertType == "LATE_RESPONSE") {
        title = this.$t("general.lateResponse");
        icon = "LoaderIcon";
        variant = "warning";

        message = document.message?.[locale]?.replace(
          "{{operatorName}}",
          this.userData.firstName + " " + this.userData.lastName
        );
        message = message?.replace("{{countLateReplyMessage}}", document?.countUnread);
        message = message?.replace("{{channelName}}", document?.channelName);
        text = message;
      } else if (document.alertType == "MISSED_RESPONSE") {
        title = this.$t("general.missedResponse");
        icon = "AlertTriangleIcon";
        variant = "danger";

        message = document.message?.[locale]?.replace(
          "{{operatorName}}",
          this.userData.firstName + " " + this.userData.lastName
        );
        message = message?.replace("{{countLateReplyMessage}}", document?.countUnread);
        message = message?.replace("{{channelName}}", document?.channelName);
        text = message;
      } else {
        title = this.$t("general.newMessage");
        icon = "MessageSquareIcon";
        variant = "success";
        message = document.voiceMsg.message?.[locale]?.replace(
          "{{operatorName}}",
          this.userData.firstName + " " + this.userData.lastName
        );
        text = message;
      }

      this.getTts(message, locale);

      this.$toast({
        component: ToastificationAvatar,
        position: "top-right",
        timeout: 10000,
        props: {
          title,
          icon,
          variant,
          text,
        },
      });
    },
    flashVoiceAlertServiceToOperator(locale, document) {
      // console.log('flashVoiceAlertServiceToOperator>document>>', document);

      const { message, actionVoiceCode, serviceName } = document;
      let textToSpeech = '';

      const operatorName = `${this.userData?.firstName ?? ''} ${this.userData?.lastName ?? ''}`.trim();

      if (actionVoiceCode === 'action.voiceAlertNewServiceTicketSpecific') {
        textToSpeech = message?.[locale]?.replace('{{operatorName}}', operatorName);
      } else if (actionVoiceCode === 'action.voiceAlertPendingPriceMerchant' ) {
        textToSpeech = message?.[locale]
          ?.replace('{{operatorName}}', operatorName)
          ?.replace('{{serviceName}}', serviceName);
      } else if (actionVoiceCode === 'action.voiceAlertPendingPriceSpecificOperator' ) {
        textToSpeech = message?.[locale]
          ?.replace('{{operatorName}}', operatorName)
          ?.replace('{{serviceName}}', serviceName);
      }

      // console.log('textToSpeech>>>', textToSpeech);
      if (textToSpeech) {
        this.getTts(textToSpeech, locale);
      }
    },
    async getTts(message, locale) {
      try {
        const payload = { text: message, lang: locale };
        const response = await SpeechAlertRepository.getTts(payload);

        const speechUrl = response?.data?.data?.fileUrl;
        if (!speechUrl) {
          console.warn("No speech URL found in the response.");
          return;
        }

        const baseUrl = new URL(speechUrl).origin;
        const apiBaseUrl = window["VUE_APP_API_URL"];
        const isStaging = window["VUE_APP_CHAT_ME_MODE"] === "staging";

        this.wavSource = isStaging
          ? speechUrl
          : speechUrl?.replace(baseUrl, `${apiBaseUrl}/asr`);

        this.$refs.notifyVoiceAlert.load();
        this.$refs.notifyVoiceAlert.play();
      } catch (error) {
        console.error("Error in getTts:", error);
      }
    },
    flashForNewServiceTicket(document){
      let text = ''
      let departmentName = document?.departmentName
      if (typeof departmentName === 'object') {
        departmentName = departmentName[this.locale] || departmentName['en'] || 'Service Ticket';
      }
      if (document?.assignedOperator) {
        text = this.$t('alert.hasNewAssignedServiceTicket', {
          name: departmentName,
        });
      }
      // else {
      //   text = this.$t('alert.hasNewServiceTicket', {
      //     name: document?.departmentName || 'Service Ticket'
      //   });
      // }
      // console.log('flashForNewServiceTicket', text);

      if (text) {
        this.$toast({
          component: ToastificationAvatar,
          position: "top-right",
          timeout: 10000,
          props: {
            title: this.$t("alert.newServiceTicket"),
            icon: `CastIcon`,
            variant: "success",
            text,
          },
        });
      }
    },
    flashHandoverServiceTicket(document){
      this.$toast({
        component: ToastificationAvatar,
        position: "top-right",
        timeout: 10000,
        props: {
          title: this.$t("alert.serviceTicketHandover"),
          icon: `CastIcon`,
          variant: "success",
          text: this.$t('alert.serviceTicketHandoverAssigned', {
            handoverAmount: document?.handoverAmount || 0,
            name: document?.departmentName || 'Service Handover'
          })
        },
      });
    },
    async readNotification(notification, idx) {
      if (!notification.isRead) {
        if (this.unreadNotificationCount > 0) {
          this.unreadNotificationCount -= 1;
        }

        NotificationRepository.read({
          notificationId: notification.notificationId,
        });
      }

      if (notification.dataType === "channel") {
        this.$router.push({
          name: "chat",
          query: {
            type: "allConversation",
            channel: notification.dataId,
          },
        });
        if (this.$route.name === "chat") {
          bus.$emit("notificationClick", "chat");
        }
      } else if (notification.dataType === "ticket") {
        this.$router.push({
          name: "ticket",
          query: {
            id: notification.dataId,
          },
        });
        if (this.$route.name === "ticket") {
          bus.$emit("notificationClick", "ticket");
        }
      } else if (notification.dataType === "channel_detail") {
        this.$router.push({
          name: "chat",
          query: {
            channel: notification.dataId,
          },
        });
        if (this.$route.name === "chat") {
          bus.$emit("notificationClick", "chat");
        }
      } else if (notification.dataType === "operator") {
        this.$router.push({
          name: "update-operator",
          params: {
            id: notification.dataId,
          },
        });
        if (this.$route.name === "update-operator") {
          bus.$emit("notificationClick", "operator");
        }
      } else if (notification.dataType === "service_system") {
        if (!notification?.service?.id) return;
        this.$router.push({
          name: 'service-system',
          query: {
            status: null,
            from: 'chat',
            serviceSystemId: notification.service.id,
          },
        });
        if (this.$route.name === "service-system") {
          bus.$emit("notificationClick", "service");
        }
      }

      this.$refs.navdropdown.hide();
    },
    async readAllNotification() {
      this.notifications = this.notifications.map((element) => {
        if (!element.isRead) {
          element.isRead = true;
        }

        return element;
      });
      this.unreadNotificationCount = 0;

      NotificationRepository.readall();

      // this.$refs.navdropdown.hide();
    },
    getNotificationTitle(notification) {
      let { locale } = this.$i18n;
      if (locale == "zh-cn") {
        locale = "cn";
      }
      const index = notification.translate.findIndex(
        (element) => element.locale == locale
      );

      if (index !== -1) {
        return notification.translate[index].title;
      }

      const indexEn = notification.translate.findIndex(
        (element) => element.locale == "en"
      );
      return notification.translate[indexEn].title;
    },
    getNotificationDescription(notification) {
      let { locale } = this.$i18n;
      if (locale == "zh-cn") {
        locale = "cn";
      }
      const index = notification.translate.findIndex(
        (element) => element.locale == locale
      );

      if (index !== -1) {
        return notification.translate[index].message;
      }

      const indexEn = notification.translate.findIndex(
        (element) => element.locale == "en"
      );
      return notification.translate[indexEn].title;
    },
    index($state = null) {
      this.messageFilter.operator = this.isAutoAssignOrRanking || false;
      NotificationRepository.index({
        limit: this.limit,
        page: this.page,
        order: "createdAt",
        sort: "desc",
        ...this.messageFilter,
      })
        .then((response) => {
          this.page += 1;

          const data = response?.data?.data;

          if (data) {
            if ($state && data.list.length == 0) {
              $state.complete();
            }

            if (this.isAutoAssignOrRanking) {
              const found = data.list.filter((ele) => {
                return (
                  ele?.operatorAss ||
                  ele?.operatorAss == "" ||
                  ele?.dataType == "ticket"
                );
              });
              this.notifications = [...this.notifications, ...found];
            } else {
              this.notifications = [...this.notifications, ...data.list];
            }
          }
        })
        .catch()
        .then(() => {
          if ($state) {
            $state.loaded();
          }
        });
    },
    async countUnread() {
      if (!this.userData.isOffline) {
        // if operator offline=true, they're can have all notification
        const response = await ChatAssignmentRepository.show();
        const { data } = response.data;
        this.isAutoAssignOrRanking = data.auto || data.autoRanking;
        this.messageFilter.operator = this.isAutoAssignOrRanking || false;
      }
      NotificationRepository.countUnread({
        ...this.messageFilter,
      })
        .then((response) => {
          const data = response?.data?.data;
          if (data) {
            this.unreadNotificationCount = data.unreadNotification;
          }
        })
        .catch();
    },
    // async getCountUnseen() {
    //   ServiceSystemRepository.getUnseenCount()
    //     .then((response) => {
    //       const data = response?.data?.data;
    //       if (data) {
    //         this.unseenServiceTicket = data?.unseenCount || 0;
    //       }
    //     })
    //     .catch();
    // },
    countUnreadConversation() {
      ChatChannelRepository.countUnread()
        .then((response) => {
          const data = response?.data?.data;
          if (data) {
            this.unreadConversation = data.countUnread;

            bus.$emit("unreadConversation", this.unreadConversation);
          }
        })
        .catch()
        .then(() => {});
    },
    goToNotificationSetting() {
      this.$refs.navdropdown.hide();
      this.$router.push({ name: "account-setting", query: { tabIndex: 2 } });
    },
  },
  setup() {
    const appStore = useAppStore();
    return {
      appStore,
    };
  },
};
</script>

<style>
.scrollable-container {
  position: relative;
}

.spinner-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
</style>
<style lang="scss" scoped>
.dropdown-menu-media {
  width: 350px;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  display: block;
}
</style>

